import { useChat } from '@ai-sdk/react';
import { useQueryClient } from '@tanstack/react-query';
import { ChatStatus } from 'ai';
import { useEffect, useRef, useState } from 'react';
import LangEnum from '../../../lang/LangEnum';
import {
  isRachelChatQueryKey,
  isRachelChatsListQueryKey,
} from '../../../services/modules/rachelChats/rachelChats.keys';
import {
  abortRachelTurn,
  clearRachelActiveContext,
  getRachelActiveContext,
  getRachelChatMessages,
  setRachelActiveContext,
} from '../../../services/modules/rachelChats/rachelChats.repository';
import { useApplicationContext } from '../../context/ApplicationContext/ApplicationContext';
import {
  getDraftRachelChatInstance,
  getOrCreateRachelChatInstance,
  setRachelChatCallbacks,
} from '../chat/rachelChatInstances';
import { useRachelDataHandler } from '../effects/useRachelDataHandler';
import { RACHEL_ASK_USER_TOOL_NAME } from '../rachel.consts';
import { RachelAskUserOutput } from '../rachel.contract';
import {
  RachelChatStatus,
  RachelOutgoingMessage,
  RachelUIMessage,
  UseRachelChatReturn,
} from '../rachel.types';
import { RachelPageContext } from '../rachelPageContext.types';
import { getRachelMessageReference } from '../rachelPageContext.utils';
import { useRachelStore } from '../store/rachel.store';
import { useRachelActiveChatId } from './useRachelActiveChatId';
import {
  getReplacementPageContext,
  hasNoPageContextToDetach,
  isPageContextDismissedNow,
  shouldDetachDismissedPageContext,
  waitForPendingDetach,
} from './useRachelChat.utils';
import { PendingPageContextDetach } from './useRachelChat.utils.types';
import { useRachelCurrentPageContext } from './useRachelCurrentPageContext';
import { useRachelErrorReporter } from './useRachelErrorReporter';
import { useRachelPageContextDismissal } from './useRachelPageContextDismissal';

const CHAT_STATUS_MAP: Record<ChatStatus, RachelChatStatus> = {
  submitted: RachelChatStatus.SUBMITTED,
  streaming: RachelChatStatus.STREAMING,
  ready: RachelChatStatus.READY,
  error: RachelChatStatus.ERROR,
};

const loadRachelChat = async (chatId: string) => {
  const [messagesResponse, contextResponse] = await Promise.all([
    getRachelChatMessages(chatId),
    getRachelActiveContext(chatId),
  ]);

  if (!messagesResponse.ok || !messagesResponse.data) {
    throw messagesResponse;
  }
  if (!contextResponse.ok) {
    throw contextResponse;
  }

  return { messages: messagesResponse.data, pageContext: contextResponse.data ?? null };
};

export const useRachelChat = (): UseRachelChatReturn => {
  const onData = useRachelDataHandler();
  const { reportRachelError } = useRachelErrorReporter();
  const queryClient = useQueryClient();
  const { team } = useApplicationContext();
  const activeChatId = useRachelActiveChatId();
  const currentPageContext = useRachelCurrentPageContext();
  const { isPageContextDismissed, dismissPageContext } =
    useRachelPageContextDismissal(currentPageContext);
  const [attachedPageContext, setAttachedPageContextState] = useState<RachelPageContext | null>(
    null,
  );
  const attachedPageContextRef = useRef<RachelPageContext | null>(null);
  const [hasLoadedAttachedPageContext, setHasLoadedAttachedPageContext] = useState(false);
  const selectChatSeqRef = useRef(0);

  const setAttachedPageContext = (pageContext: RachelPageContext | null) => {
    attachedPageContextRef.current = pageContext;
    setAttachedPageContextState(pageContext);
  };
  const pendingDetachRef = useRef<PendingPageContextDetach | null>(null);

  useEffect(() => {
    setRachelChatCallbacks({
      onData,
      onError: (chatError) => {
        if (chatError.message.includes(LangEnum.API_EXCEPTION_RACHEL_CHAT_BUSY)) {
          reportRachelError(chatError, LangEnum.API_EXCEPTION_RACHEL_CHAT_BUSY);
        }
      },
      onFinish: (event, chatId) => {
        void queryClient.invalidateQueries({
          predicate: (query) => isRachelChatsListQueryKey(query.queryKey),
        });
        if (event.isAbort) {
          useRachelStore.getState().markMessageInterrupted(event.message.id);
        }
        if (!chatId) {
          return;
        }
        void queryClient.invalidateQueries({
          predicate: (query) => isRachelChatQueryKey(query.queryKey, chatId),
        });
        const { getActiveChatIdForTeam, markChatUnread } = useRachelStore.getState();
        if (getActiveChatIdForTeam(team?.id) !== chatId) {
          markChatUnread(chatId);
        }
      },
    });
  });

  const instance = activeChatId
    ? getOrCreateRachelChatInstance(activeChatId)
    : getDraftRachelChatInstance();

  const { messages, status, error, stop, addToolApprovalResponse, addToolOutput } =
    useChat<RachelUIMessage>({
      chat: instance,
    });

  const getActiveChatId = () => useRachelStore.getState().getActiveChatIdForTeam(team?.id);

  const selectChat = async (chatId: string) => {
    const requestSeq = ++selectChatSeqRef.current;
    setHasLoadedAttachedPageContext(false);

    const chat = await loadRachelChat(chatId);
    if (requestSeq !== selectChatSeqRef.current) {
      return;
    }

    const target = getOrCreateRachelChatInstance(chatId);
    if (target.messages.length === 0) {
      target.messages = chat.messages;
    }

    setAttachedPageContext(chat.pageContext);
    setHasLoadedAttachedPageContext(true);
    useRachelStore.getState().setActiveChatId(chatId, team?.id ?? null);
    useRachelStore.getState().clearChatUnread(chatId);
  };

  const startNewConversation = async () => {
    selectChatSeqRef.current += 1;
    setAttachedPageContext(null);
    setHasLoadedAttachedPageContext(false);
    useRachelStore.getState().clearActiveChat();
  };

  const stopActiveTurn = async () => {
    const chatId = getActiveChatId();
    const [, abortResult] = await Promise.allSettled([
      stop(),
      chatId ? abortRachelTurn(chatId) : null,
    ]);

    if (abortResult.status === 'rejected') {
      reportRachelError(abortResult.reason);
      return;
    }
    if (abortResult.value && !abortResult.value.ok) {
      reportRachelError(abortResult.value);
    }
  };

  const sendMessageToActiveChat = ({ text, files }: RachelOutgoingMessage) => {
    const chatId = getActiveChatId();
    if (!chatId) {
      throw new Error('chatId é obrigatório para enviar mensagens da Rachel');
    }
    const target = getOrCreateRachelChatInstance(chatId);
    const reference = attachedPageContextRef.current
      ? getRachelMessageReference(attachedPageContextRef.current)
      : null;
    void target.sendMessage({
      text,
      ...(files?.length ? { files } : {}),
      ...(reference ? { metadata: { reference } } : {}),
    });
  };

  const attachCurrentPage = async (chatId: string, pageContext = currentPageContext) => {
    await waitForPendingDetach(pendingDetachRef.current, chatId);
    if (isPageContextDismissedNow(pageContext)) {
      return;
    }
    const response = await setRachelActiveContext(chatId, pageContext);
    if (!response.ok || !response.data) {
      throw response;
    }
    if (getActiveChatId() !== chatId) {
      return;
    }
    setAttachedPageContext(response.data);
    setHasLoadedAttachedPageContext(true);
  };

  const clearAttachedPageContext = async (chatId: string) => {
    const response = await clearRachelActiveContext(chatId);
    if (!response.ok) {
      throw response;
    }
    if (getActiveChatId() === chatId) {
      setAttachedPageContext(null);
    }
  };

  const detachPageContext = (chatId: string): Promise<void> => {
    const pendingDetach = pendingDetachRef.current;
    if (pendingDetach?.chatId === chatId) {
      return pendingDetach.promise;
    }
    if (hasNoPageContextToDetach({ hasLoadedAttachedPageContext, attachedPageContext })) {
      return Promise.resolve();
    }

    const promise = clearAttachedPageContext(chatId).finally(() => {
      if (pendingDetachRef.current?.promise === promise) {
        pendingDetachRef.current = null;
      }
    });
    pendingDetachRef.current = { chatId, promise };
    return promise;
  };

  const replacementPageContext = getReplacementPageContext({
    activeChatId,
    hasLoadedAttachedPageContext,
    attachedPageContext,
    currentPageContext,
    isPageContextDismissed,
  });

  const currentTeamId = team?.id;
  const selectChatRef = useRef(selectChat);
  const startNewConversationRef = useRef(startNewConversation);
  const reportRachelErrorRef = useRef(reportRachelError);
  const detachPageContextRef = useRef(detachPageContext);
  useEffect(() => {
    selectChatRef.current = selectChat;
    startNewConversationRef.current = startNewConversation;
    reportRachelErrorRef.current = reportRachelError;
    detachPageContextRef.current = detachPageContext;
  });

  const shouldDetachPageContext = shouldDetachDismissedPageContext({
    activeChatId,
    hasLoadedAttachedPageContext,
    attachedPageContext,
    isPageContextDismissed,
  });
  useEffect(() => {
    if (!shouldDetachPageContext || !activeChatId) {
      return;
    }
    detachPageContextRef.current(activeChatId).catch((error: unknown) => {
      reportRachelErrorRef.current(error, LangEnum.RACHEL_PAGE_CONTEXT_UPDATE_FAILED);
    });
  }, [shouldDetachPageContext, activeChatId]);

  const hasSyncedRef = useRef(false);
  const lastSyncedTeamIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (hasSyncedRef.current && lastSyncedTeamIdRef.current === currentTeamId) {
      return;
    }
    const isFirstSync = !hasSyncedRef.current;
    hasSyncedRef.current = true;
    lastSyncedTeamIdRef.current = currentTeamId;

    const chatIdForTeam = useRachelStore.getState().getActiveChatIdForTeam(currentTeamId);
    if (chatIdForTeam) {
      void selectChatRef.current(chatIdForTeam).catch((error) => {
        reportRachelErrorRef.current(error);
      });
      return;
    }

    if (!isFirstSync) {
      void startNewConversationRef.current();
    }
  }, [currentTeamId]);

  return {
    messages,
    status: CHAT_STATUS_MAP[status],
    error,
    isStreaming: status === 'streaming' || status === 'submitted',
    currentPageContext,
    replacementPageContext,
    isPageContextDismissed,
    dismissPageContext,
    stop: stopActiveTurn,
    sendMessage: sendMessageToActiveChat,
    addToolApprovalResponse: (id: string, isApproved: boolean) =>
      addToolApprovalResponse({ id, approved: isApproved }),
    addAskUserAnswer: (toolCallId: string, output: RachelAskUserOutput) => {
      void addToolOutput({ tool: RACHEL_ASK_USER_TOOL_NAME, toolCallId, output });
    },
    selectChat,
    startNewConversation,
    attachCurrentPage,
    detachPageContext,
  };
};
